import { useMemo, useState } from "react";
import { BookText, RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PV } from "@/constants/testIds";
import {
  MASTER_PLACEHOLDER_COUNT,
  PLACEHOLDER_VOCABULARY,
  STATUS_FILTERS,
  STATUS_LABELS,
  filterPlaceholderVocabulary,
} from "@/lib/placeholderVocabulary";
import "@/styles/emr-catalog.css";

function statusClass(status) {
  if (status === "ready") return "emr-catalog__status emr-catalog__status--active";
  if (status === "transform") return "emr-catalog__status emr-catalog__status--transform";
  return "emr-catalog__status emr-catalog__status--inactive";
}

export default function PlaceholderVocabularyPage() {
  const [placeholderQuery, setPlaceholderQuery] = useState("");
  const [meaningQuery, setMeaningQuery] = useState("");
  const [status, setStatus] = useState("all");

  const rows = useMemo(
    () =>
      filterPlaceholderVocabulary(PLACEHOLDER_VOCABULARY, {
        placeholderQuery,
        meaningQuery,
        status,
      }),
    [placeholderQuery, meaningQuery, status],
  );

  const clearFilters = () => {
    setPlaceholderQuery("");
    setMeaningQuery("");
    setStatus("all");
  };

  return (
    <div data-testid={PV.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={PV.title} className="emr-catalog__title">
            Placeholder Vocabulary
          </h1>
          <p className="emr-catalog__subtitle">
            Read-only reference of EMRbyMD dynamic placeholders. This is not a report designer,
            PDF editor, or SmartPhrase editor.
          </p>
        </div>
      </div>

      <div className="emr-catalog__workspace">
        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Purpose</h2>
          <p className="emr-catalog__copy">
            Canonical tokens from the EMRbyMD placeholder vocabulary. {MASTER_PLACEHOLDER_COUNT}{" "}
            master placeholders are listed, plus reserved names that are not implemented yet.
            Values cannot be edited here.
          </p>
        </section>

        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Filters</h2>
          <div className="emr-catalog__filters emr-catalog__filters--search-row emr-catalog__filters--vocab">
            <div className="emr-catalog__search">
              <Search className="emr-catalog__search-icon" aria-hidden="true" />
              <input
                data-testid={PV.searchPlaceholder}
                value={placeholderQuery}
                onChange={(event) => setPlaceholderQuery(event.target.value)}
                placeholder="Search by placeholder..."
                className="emr-catalog__input"
                aria-label="Search by placeholder"
              />
            </div>
            <div className="emr-catalog__search">
              <Search className="emr-catalog__search-icon" aria-hidden="true" />
              <input
                data-testid={PV.searchMeaning}
                value={meaningQuery}
                onChange={(event) => setMeaningQuery(event.target.value)}
                placeholder="Search by meaning..."
                className="emr-catalog__input"
                aria-label="Search by meaning"
              />
            </div>
            <select
              aria-label="Status"
              data-testid={PV.statusFilter}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="emr-catalog__select emr-catalog__select--wide"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              data-testid={PV.clearFilters}
              onClick={clearFilters}
              className="btn emr-catalog__btn emr-catalog__btn--ghost"
            >
              <RotateCcw className="emr-catalog__btn-icon" />
              Clear Filters
            </Button>
          </div>
        </section>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Vocabulary</h2>
          <p data-testid={PV.count} className="emr-catalog__copy">
            Showing {rows.length} of {PLACEHOLDER_VOCABULARY.length} tokens.
          </p>
          {rows.length === 0 ? (
            <div data-testid={PV.emptyState} className="emr-catalog__empty">
              <BookText className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No placeholders match these filters.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={PV.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Placeholder</th>
                    <th>Meaning</th>
                    <th>Source</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr
                      key={item.placeholder}
                      data-testid={PV.row}
                      data-placeholder={item.placeholder}
                    >
                      <td>
                        <code className="emr-catalog__token emr-catalog__token--placeholder">
                          {item.placeholder}
                        </code>
                      </td>
                      <td>{item.meaning}</td>
                      <td className="emr-catalog__muted">{item.source}</td>
                      <td>
                        <span className={statusClass(item.status)}>{STATUS_LABELS[item.status]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
